import { inject, Injectable} from "@angular/core";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { collection, doc, Firestore, getDocs, onSnapshot, query, serverTimestamp, Timestamp, where } from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { User } from "@interfaces/user.i";
import { arrayUnion } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  defer,
  delay,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  retryWhen,
  scan,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  throwError,
  timeout
} from "rxjs";
import { CommonVariable } from "../app.constants";
import { MyToastbarComponent } from "../components/my-toastbar/my-toastbar.component";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";
import { SafeLocalStorageService } from "./safe-local-storage.service";
import { environment } from "src/environments/environment";
import { Platform } from "@ionic/angular";

@Injectable({
  providedIn: "root",
})
export class UserManagementService {
  isLoggedIn$ = new ReplaySubject(1);
  selectedCurrency: string;
  private safeLocalStorage = inject(SafeLocalStorageService);
  selectedlanguage$ = new BehaviorSubject(this.safeLocalStorage.getItem("language_code") || "");
  userRequest$ = new ReplaySubject(1);
  userRequestForHome$ = new BehaviorSubject(null);
  userRequestForHomeTopPage$ = new BehaviorSubject(null);
  private destroy$ = new Subject<void>();
  platform = inject(Platform);
  private authStateService = inject(AuthStateService);
  private languageChangeSubject = new Subject<{ previous_language: string; new_language: string; page: string }>();

  userDetailsForHome$: Observable<User> = this.userRequestForHomeTopPage$.pipe(
    switchMap((user: Data) => {
      return user
        ? this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of(null);

            // Gate on customTokenReady$ before opening the user-document listener.
            // This prevents the "permission-denied" race condition on iOS Safari
            // where onSnapshot fires before the Firebase custom token is available.
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => {
                const documentRef = doc(this.firestore, `users/${user?.uid}`);

                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    documentRef,
                    (docSnapshot) => observer.next({ payload: { data: () => docSnapshot.data() } }),
                    (error) => {
                      if ((error as { code?: string })?.code === "permission-denied") {
                        observer.next(null);
                        observer.complete();
                        return;
                      }
                      observer.error(error);
                    }
                  );
                  return () => unsubscribe();
                }).pipe(
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError,
                  shareReplay(1)
                );
              })
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        ).pipe(map((res: any) => res?.payload?.data()))
        : of(null);
    }),
    tap((user) => {
      if (!user) return;
      if (typeof localStorage === "object") {
        try {
          if (user["wp_user_id"]) {
            localStorage.setItem("wp_user_id", user ? user["wp_user_id"] : null);
          }
          localStorage.setItem(
            "swUpdatedAt",
            user && user["swUpdatedAt"] ? user["swUpdatedAt"].toDate() : null
          );
          localStorage.setItem("sw", user ? user["sw_hash"] : null);
          // setItem stringifies, so an absent email would be stored as "undefined"/"null".
          const userEmail = typeof user["email"] === "string" ? user["email"].trim() : "";
          if (userEmail) {
            localStorage.setItem("email", userEmail);
          } else {
            localStorage.removeItem("email");
          }
        } catch (e) {
          Storage.prototype._setItem = Storage.prototype.setItem;
          Storage.prototype.setItem = function () { };
          this
            .getConstantsMsg("1083")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1:
                    "Your web browser does not support storing settings locally. In Safari, the most common cause of this is using 'Private Browsing Mode'. Some settings may not save or some features may not work properly for you.",
                  line_2: "",
                },
                style: {
                  icon: "tick circle alert.svg",
                  type: "warning",
                },
              };
              this.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((err) => {
              return err;
            });
        }
      }
    }),
    shareReplay(1)
  );

  public getConstantsMsg(msgCode) {
    return new Promise<any>((resolve, reject) => {
      try {
        // let toastMsgData;
        this.firestoreDataService
          .getAngularFireStore(`/Global_lookup/toast_message/list/${msgCode}`, "get")
          ?.subscribe((msg: Data) => {
            // This variable contains the massage data
            const msgExists = msg?.exists;
            const msgData = msg?.data();
            //  This Condition checks if the message data null and undefined
            if (msgExists != null && msgExists != undefined && msgData != null && msgData != undefined) {
              const msgData = JSON.parse(JSON.stringify(msg.data()));
              let toastMsg: string;
              if (msgData?.message?.[this.selectedlanguage$?.value]) {
                toastMsg = msgData?.message?.[this.selectedlanguage$?.value];
              } else if (msgData?.message?.[CommonVariable?.preferredLanguage]) {
                toastMsg = msgData?.message?.[CommonVariable?.preferredLanguage];
              }
              const msgStyle = msgData?.style;
              const toastMsgData = {
                message: toastMsg ? toastMsg : {},
                style: msgStyle,
              };
              resolve(toastMsgData);
            } else {
              resolve(null);
            }
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  public showToastMessage(translatedMsg, defaultMsg): void {
    const message = translatedMsg?.message ? translatedMsg?.message : defaultMsg?.message;
    const icon = translatedMsg?.style?.icon ? translatedMsg?.style?.icon : defaultMsg?.style?.icon;
    const snackType = translatedMsg?.style?.type ? translatedMsg?.style?.type : defaultMsg?.style?.type;
    const duration = translatedMsg?.style?.duration ? translatedMsg?.style?.duration : 2000;
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: duration,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, duration: duration, icon: icon, state: snackType },
    });
  }

  constructor(
    private catchService: CatchErrorService,
    private firestore: Firestore,
    private firestoreDataService: FirestoreDataService,
    private snackBar: MatSnackBar,
    private commonService: CommonService

  ) {
    this.selectedCurrency = this.safeLocalStorage.getItem("preferred_currency_code") || CommonVariable.preferredCurrency;
    this.languageChangeSubject
      .pipe(
        takeUntil(this.destroy$), // Ensure cleanup happens first
        throttleTime(2000) // Then throttle requests
      )
      .subscribe(({ previous_language, new_language, page }) => {
        this.callSetUserLanguageLogInFb(previous_language, new_language, page);
      });
  }

  updateNewlyAddedCards(): Observable<void | Error> {
    return this.commonService.getUid("UserManagementService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return throwError(() => new Error("No userId found"));
        }
        const path_id = "KdpTQsnyjKvgqvjItVvT";
        const doc_data = { card: [] };
        const method = "update";

        return this.commonService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        });
      })
    );
  }

 // The following function is used to update the user's location details
  updateUserLocationDetails(userDetails: Data): Observable<Data> {
    return this.commonService.getUid('updateUserLocationDetails').pipe(
      take(1),
      switchMap((userId) => {
        if (userId) {
          return of(userId);
        }
      }),
      switchMap((userId) => {
        if (
          !userId ||
          !userDetails ||
          typeof userDetails !== 'object' ||
          Array.isArray(userDetails)
        ) {
          return of(null);
        }
        const date =
          new Date().toDateString() + ' ' + new Date().toTimeString();

        const locationData = {
          ...userDetails,
          createdDate: new Date(),
        };
        const path_id = 'hS2pb7zofds4dD1OFqkO';
        const dv = { docId: date };
        const method = 'create';
        return this.firestoreDataService
          .getV2CloudFunction('updatefirestoredoc', {
            path_id,
            doc_data: locationData,
            dv,
            method,
          })
          .pipe(
            catchError(() => {
              return of(null);
            })
          );
      }),
    );
  }

  /**
   * This code is used to get the user location details from the user location info collection
   */

  public locations$ = this.isLoggedIn$.pipe(
    debounceTime(10),
    switchMap((user: Data) => {
      return this.updateUserLocationDetails(CommonVariable.locationInfo);
    })
  );
  // this code updates the login status of the user
  // it is used by the login component to redirect the user to the main page
  // if they are already logged in
  updateIsLoggedIn(user) {
    this.isLoggedIn$.next(user);
  }

    /** Shared in-flight registration IP prefetch (ipify). Reused on Turnstile submit cache miss. */
  private registrationIpPrefetch$: Observable<string> | null = null;

  // This function is to get user country base on IP address
  // this function will call ipStackAPI function in firebase functions
  // and will return user country base on IP address

  private persistPublicIp(ip: string | null | undefined): void {
    if (!ip || ip === "0.0.0.0") {
      return;
    }
    try {
      localStorage.setItem("ip", ip);
    } catch {
      // ignore quota / private-mode failures
    }
    CommonVariable.userIP = ip;
  }

  private fetchPublicIP(): Observable<string> {
    return from(fetch("https://api.ipify.org?format=json")).pipe(
      // Prevent a hung ipify request from permanently occupying registrationIpPrefetch$.
      timeout({
        each: 2500,
        with: () => throwError(() => new Error("ipify timeout")),
      }),
      switchMap((response: Response) => {
        if (!response?.ok) {
          return of({ ip: "0.0.0.0" } as { ip: string });
        }
        return from(response.json() as Promise<{ ip: string }>);
      }),
      map((data) => {
        const ip = data?.ip || "0.0.0.0";
        this.persistPublicIp(ip);
        return ip;
      }),
      catchError((error) => {
        return of("0.0.0.0");
      }),
      shareReplay(1)
    );
  }

  /**
   * Single shared ipify prefetch for registration. Call from page init (fire-and-forget)
   * and reuse on Turnstile submit when localStorage.ip is missing.
   * Invalid/fallback results clear the shared cache so a later submit can retry.
   */
  public prefetchRegistrationIp$(): Observable<string> {
    if (!this.registrationIpPrefetch$) {
      this.registrationIpPrefetch$ = this.fetchPublicIP().pipe(
        tap((ip) => {
          if (!ip || ip === "0.0.0.0") {
            this.registrationIpPrefetch$ = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.registrationIpPrefetch$;
  }

  /** Fire-and-forget warm of localStorage.ip before Turnstile submit. Errors swallowed. */
  public warmRegistrationIpCache(): void {
    this.prefetchRegistrationIp$().pipe(take(1)).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /** Test helper — clears shared prefetch so the next warm starts a new request. */
  public resetRegistrationIpPrefetchForTests(): void {
    this.registrationIpPrefetch$ = null;
  }

  
  public getUserCountryBaseOnIP(): Observable<Data> {
    if (CommonVariable?.userIPstackData && Object.keys(CommonVariable.userIPstackData).length > 0) {
      return of(CommonVariable.userIPstackData as Data);
    }

    // Default fallback data to prevent blocking
    const defaultData: Data = {
      country_code: "GB",
      country_name: "United Kingdom",
      currency: { code: "GBP", symbol: "£" },
      location: { calling_code: "44", languages: [{ code: "en" }] },
    };

    // Public auth pages (login/signup) have no Firebase user. The CF requires
    // auth.uid, so calling it unauthenticated only produces retries/errors and
    // can block language + login CTA. Use defaults until after login.
    return this.commonService.getUid("UserManagementService").pipe(
      switchMap((userId) => {
        if (!userId) {
          CommonVariable.userIPstackData = defaultData;
          return of(defaultData);
        }

        return this.fetchPublicIP().pipe(
          switchMap((userIP) =>
            this.getV2CloudFunction("getandcachelocationforuseripv2", {
              userId,
              userIP,
            }).pipe(
              // Add 3-second timeout to prevent blocking page load
              timeout(3000),
              map((data: Data) => {
                const ipStackData = data?.data ?? {};
                if (Object.keys(ipStackData).length === 0) {
                  return defaultData;
                }
                CommonVariable.userIPstackData = ipStackData;
                return ipStackData as Data;
              }),
              catchError((error) => {
                // Return fallback object on error or timeout
                console.warn('IP location lookup failed or timed out, using GB defaults:', error?.message || error);
                CommonVariable.userIPstackData = defaultData;
                return of(defaultData);
              }),
              shareReplay(1)
            )
          )
        );
      }),
      // Add overall timeout as a safety net
      timeout(3500),
      catchError((error) => {
        console.warn('getUserCountryBaseOnIP timed out, using GB defaults');
        CommonVariable.userIPstackData = defaultData;
        return of(defaultData);
      })
    );
  }



  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  // This function is used to update the user's social connect information in the database.
  // It is called when a user visits the social connect page.
  updateUserSocialConnectInfo() {
    return this.commonService.getUid("").pipe(
      take(1), // take only once
      switchMap(async (uid: string) => {
        if (!uid) {
          throw new Error("UID not found");
        }
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { Is_social_shown: true };
        const method = "update";

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (error) => {
            console.error("Error updating social connect info:", error);
          }
        });
      }),
      catchError((err) => {
        console.error("Error updating social connect info:", err);
        return of(err);
      })
    );
  }

  getSelectedCurrency(): string {
    return this.selectedCurrency;
  }

  /** add user id to existing fingerprint in firebase database */
  addUseridToFingerprint(userId: string, deviceId: Data) {
    const path_id = "LO9CInOlAYitQuhT0aDT";
    const doc_data = { userId: arrayUnion(userId) };
    const dv = { docId: deviceId };
    const method = "update";

    return this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (err) => {
        console.error('Error updating push notification data:', err);
        return err;
      }
    });
  }

  // This method used to upload user device id
  uploadUserDeviceId(userId: string, deviceId) {
    if (!userId || typeof userId !== 'string') throw new Error("uploadUserDeviceId : Unable to upload the device id.");
    this.firestoreDataService
      .getAngularFireStore(`${environment.mirrorPath}fingerprint-users/${deviceId}`, "snapshotChanges")
      ?.pipe(take(1))
      ?.subscribe((data: Data) => {
        let fingerPrint;
        //if userId exists
        if (data?.payload?.exists) {
          fingerPrint = data?.payload?.data()["userId"];
          if (fingerPrint?.indexOf(userId) == -1) {
            this.addUseridToFingerprint(userId, deviceId);
            this.checkFruadUser(userId, deviceId);
          } else {
            this.updateLastDetected(userId, deviceId);
          }
        } else {
          // if device does not exist
          const deviceInfo = {
            device_details: {
              IP: CommonVariable.userIP,
              browser: navigator.userAgent,
              device_type: CommonVariable.device.model,
              first_detected: serverTimestamp(),
              last_detected: serverTimestamp(),
            },
            userId: [userId],
          };

          const path_id = "LO9CInOlAYitQuhT0aDT";
          const doc_data = deviceInfo;
          const dv = { docId: deviceId };
          const method = "update";

          this.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            next: () => {
              this.checkFruadUser(userId, deviceId);
            },
            error: (err) => {
              console.error('Error updating deviceInfo data in fingerprint-users:', err);
            }
          });
        }
      });
  }

  /**
   * Checks if a user is potentially fraudulent by querying for multiple devices with the same user ID
   * @param userId The user ID to check
   * @param deviceId The current device fingerprint ID
   */
  checkFruadUser(userId: string, deviceId: any): void {
    try {
      // First let the JavaScript event loop clear by using Promise resolution
      Promise.resolve().then(async () => {
        try {
          const collectionRef = collection(this.firestore, "fingerprint-users");
          const q = query(collectionRef, where("userId", "array-contains", userId));
          const snapshot = await getDocs(q);

          if (snapshot && snapshot.size > 1) {
            const path_id = "UHlTRauANXSNjpsVmUnY";
            const doc_data = { userId: arrayUnion(userId) };
            const method = "update";

            this.getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              method,
            }).subscribe({
              error: (err) => {
                console.error('Error updating deviceInfo data in fingerprint-users:', err);
              }
            });
          }
        } catch (err) {
          return err;
        }
      });
    } catch (error) {
      return error;
    }
  }

  /**
   * Updates the last_detected field in the device_details field of a document in the fingerprint-users collection
   * @param userId the id of the user to update
   * @param deviceId the id of the device to update
   */
  updateLastDetected(userId: string, deviceId: Data) {
    const path_id = "LO9CInOlAYitQuhT0aDT";
    const doc_data = {
      device_details: {
        last_detected: Timestamp.now(),
      },
    };
    const dv = { docId: deviceId };
    const method = "update";

    this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (err) => {
        console.error('Error updating push notification data:', err);
      }
    });
  }

  public setUserLanguageLogInFb(previous_language: string, new_language: string, page: string) {
    this.languageChangeSubject.next({ previous_language, new_language, page });
  }

  // This method is called to add logs in FB whenever local storage value for language_code changes in the app
  private callSetUserLanguageLogInFb(
    previous_language: string,
    new_language: string,
    page: string
  ) {
    if (previous_language === new_language) return;

    const ipData: Data = CommonVariable.userIPstackData;

    defer(() =>
      this.commonService.getUid("").pipe(
        take(1), // only once
        switchMap((uid: string | null) => {
          const payload = {
            userId: uid,
            previous_language,
            new_language,
            page,
            user_ipaddress: ipData?.ip || "",
            other_details: ipData,
          };

          return this.getV2CloudFunction("languagelogsupdate", payload);
        }),
        takeUntil(this.destroy$)
      )
    ).subscribe({
      error: (e) => {
        console.error("Error in language logs update:", e);
      }
    });
  }





  ngOnDestroy() {
    if (this.destroy$) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
  /**
   * Evaluate fingerprint quality using FingerprintJS where available.
   * Returns `{ confidence: 'low'|'medium'|'high', fallbackUsed: boolean }`.
   *
   * Rules:
   * - Primary signal: result.confidence.score (0–1 probability)
   * - fallbackUsed reflects missing score / component heuristic, not score quality
   * - Component-count heuristic is used only when score is unavailable
   */
  private evaluateFingerprintConfidenceFromResult(result: any): {
    confidence: 'low' | 'medium' | 'high';
    fallbackUsed: boolean;
  } {
    const MIN_COMPONENTS_LOW = 6;
    const MIN_COMPONENTS_MED = 10;

    try {
      // visitorId-only string is not a GetResult — caller must resolve a real result first
      if (typeof result === 'string') {
        return { confidence: 'low', fallbackUsed: true };
      }

      const score = result?.confidence?.score;

      if (typeof score === 'number') {
        if (score < 0.4) {
          return { confidence: 'low', fallbackUsed: false };
        }

        if (score < 0.7) {
          return { confidence: 'medium', fallbackUsed: false };
        }

        return { confidence: 'high', fallbackUsed: false };
      }

      const components = result?.components;
      const count = components ? Object.keys(components).length : 0;

      if (count < MIN_COMPONENTS_LOW) {
        return { confidence: 'low', fallbackUsed: true };
      }

      if (count < MIN_COMPONENTS_MED) {
        return { confidence: 'medium', fallbackUsed: true };
      }

      return { confidence: 'high', fallbackUsed: true };
    } catch {
      return { confidence: 'low', fallbackUsed: true };
    }
  }

  private hasFingerprintConfidenceData(result: unknown): boolean {
    if (!result || typeof result === 'string') {
      return false;
    }

    const fpResult = result as Data;
    return typeof fpResult?.confidence?.score === 'number' || !!fpResult?.components;
  }

  /** Use cached GetResult when available; otherwise call fp.get() and cache it. */
  private async resolveFingerprintResult(): Promise<Data> {
    const cached = CommonVariable.FingerprintJSResult;
    if (this.hasFingerprintConfidenceData(cached)) {
      return cached as Data;
    }

    const fp = await FingerprintJS.load();
    const result = await fp.get();
    CommonVariable.FingerprintJSResult = result;
    if (result?.visitorId) {
      CommonVariable.FingerprintJSConst = result.visitorId;
    }
    return result as Data;
  }


  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;


  }
  // This method is used to format error messages
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return error;
  }

  // This method is used to upload user fingerprint info to firestore
  // It creates or updates a document in the user's devices subcollection
  async uploadUserFingerprintInfo(userId: string, deviceId: string): Promise<string | null> {
  // userId is required to link the fingerprint to a user, if it's not available we cannot proceed
  if(!userId) return;
  try {
    const collectionRef = collection(this.firestore, `/users/${userId}/devices`);
    const snapshot = await getDocs(collectionRef);

    const matchingDoc = snapshot.docs.find(
      d => (d.data() as Data)?.fingerprint_id === deviceId
    );

    // PLATFORM (only needed for new doc)
    const isPwa =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Data).standalone === true;
      let platformType = 'WEB';
      if (isPwa) {
        platformType = 'PWA';
      } else if (this.platform.is('ios')) {
        platformType = 'IOS';
      } else if (this.platform.is('android')) {
        platformType = 'ANDROID';
      }

    const deviceIdObj: Data = {};

    // ---- FINGERPRINT CONFIDENCE (always recomputed) ----
    try {
      const fpResult = await this.resolveFingerprintResult();
      const { confidence, fallbackUsed } =
        await this.evaluateFingerprintConfidenceFromResult(fpResult);

      deviceIdObj.fingerprint_confidence = confidence;
      deviceIdObj.fingerprint_fallback_used = fallbackUsed;
    } catch {
      deviceIdObj.fingerprint_confidence = 'low';
      deviceIdObj.fingerprint_fallback_used = true;
    }

    // ---- CONDITIONAL UPDATE ----
    if (matchingDoc) {
      // ✅ Existing device → lightweight heartbeat update
      deviceIdObj.last_seen_at = Timestamp.now();
    } else {
      // ✅ New device → full payload
      deviceIdObj.fingerprint_id = deviceId;
      deviceIdObj.first_seen_at = Timestamp.now();
      deviceIdObj.last_seen_at = Timestamp.now();
      deviceIdObj.platform = platformType;
      deviceIdObj.auth_state_at_capture = 'authenticated';
      deviceIdObj.eligible_for_fraud_checks = false;
    }

    const path_id = 'BiJdsmOVt6wWdfysSK9N';
    const docId = matchingDoc?.id ?? deviceId;
    const res: Data = await firstValueFrom(
      this.getV2CloudFunction('updatefirestoredoc', {
        path_id,
        doc_data: deviceIdObj,
        method: 'update',
        dv: { docId },
      })
    );
    return matchingDoc ? docId : res?.docId ?? null;
  } catch (err) {
    this.errorHandlerLogs('Error in uploadUserFingerprintInfo', err);
    return null;
  }
}
}
